import { useEffect, useState } from "react";
import { Link, Navigate, Outlet, useLocation, useMatch, useNavigate, useOutletContext, useParams } from "react-router-dom";
import type { AgentTheme, ConversationResponse, DocumentResponse, PolicyResponse } from "@bubble/core";
import { ChatBubble } from "../components/ChatBubble.tsx";
import { Steps } from "../components/Steps.tsx";
import { api, messageOf, type AgentResponse } from "../lib/api.ts";
import { useAuth, useToken } from "../lib/auth.tsx";
import { markStep, nextStep, previewNote, progressFor, readMarks, stepPath, type Progress, type StepId } from "../lib/setup.ts";

export type AgentOutlet = {
  agent: AgentResponse;
  reload: () => Promise<void>;
  progress: Progress;
  mark: (step: "answers" | "look" | "test") => void;
  liveTheme: AgentTheme;
  setLiveTheme: (theme: AgentTheme) => void;
  note: string;
};

const emptyProgress: Progress = {
  create: true,
  knowledge: false,
  answers: false,
  test: false,
  look: false,
  install: false,
  reading: false,
};

export function AgentLayout() {
  const { agentId } = useParams();
  const token = useToken();
  const { me, meError } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const onTest = useMatch("/app/agents/:agentId/test");
  const onLook = location.pathname.endsWith("/look");
  const [agent, setAgent] = useState<AgentResponse | null>(null);
  const [progress, setProgress] = useState<Progress>(emptyProgress);
  const [note, setNote] = useState("");
  const [liveTheme, setLiveTheme] = useState<AgentTheme | null>(null);
  const [error, setError] = useState("");
  const [progressError, setProgressError] = useState("");
  const [pending, setPending] = useState(false);
  const [tick, setTick] = useState(0);

  async function load(): Promise<void> {
    if (!agentId) return;
    const body = await api<{ agent: AgentResponse }>(`/v1/agents/${agentId}`, token);
    setAgent(body.agent);
  }

  useEffect(() => {
    if (!me?.tenant || !agentId) return;
    let active = true;
    void load().catch((caught: unknown) => {
      if (active) setError(messageOf(caught));
    });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId, token, me?.tenant]);

  useEffect(() => {
    if (!agent) return;
    setLiveTheme(agent.theme);
    // Reset the preview when opening a different agent. Edits on the look step update it themselves.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agent?.id]);

  useEffect(() => {
    if (!agent || onLook) return;
    setLiveTheme(agent.theme);
  }, [agent, onLook]);

  useEffect(() => {
    if (!agent) return;
    let active = true;
    void Promise.all([
      api<{ documents: DocumentResponse[] }>(`/v1/agents/${agent.id}/documents`, token),
      api<{ conversations: ConversationResponse[] }>(`/v1/agents/${agent.id}/conversations`, token),
      api<{ policies: PolicyResponse[] }>(`/v1/agents/${agent.id}/policies`, token),
    ])
      .then(([documents, conversations, policies]) => {
        if (!active) return;
        setProgress(
          progressFor({
            agent,
            documents: documents.documents,
            conversations: conversations.conversations,
            marked: readMarks(agent.id),
          }),
        );
        const latest = policies.policies[0];
        setNote(previewNote(agent, latest?.status === "draft" && Boolean(agent.publishedPolicyVersionId)));
      })
      .catch((caught: unknown) => {
        if (active) setProgressError(messageOf(caught));
      });
    return () => {
      active = false;
    };
  }, [agent, token, tick]);

  useEffect(() => {
    if (!progress.reading || !agent) return;
    const timer = window.setInterval(() => setTick((value) => value + 1), 3000);
    return () => window.clearInterval(timer);
  }, [progress.reading, agent]);

  if (meError) return <p className="banner bad">{meError}</p>;
  if (!me) return <p className="muted">Loading…</p>;
  if (!me.tenant) return <p className="banner">Name your business before opening an agent.</p>;
  if (error) return <p className="banner bad">{error}</p>;
  if (!agent || !liveTheme) return <p className="muted">Loading your agent…</p>;

  const current = currentStep(location.pathname);

  function mark(step: "answers" | "look" | "test") {
    if (!agent) return;
    markStep(agent.id, step);
    setProgress((current) => ({ ...current, [step]: true }));
    setTick((value) => value + 1);
  }

  async function remove() {
    if (!agent || !window.confirm(agent.isSample ? "Remove the sample cafe?" : `Delete ${agent.name} and its files?`)) return;
    setPending(true);
    try {
      await api(`/v1/agents/${agent.id}`, token, { method: "DELETE" });
      navigate("/app");
    } catch (caught) {
      setError(messageOf(caught));
      setPending(false);
    }
  }

  return (
    <div className="stack agent-shell">
      {agent.isSample ? (
        <p className="banner">
          This is a sample cafe, Harbor & Rye. It is not your business. <Link to="/app">Start your own agent</Link>
        </p>
      ) : null}
      <header className="page-head">
        <div>
          <p className="agent-name">
            <span className="swatch inline" style={{ background: liveTheme.color }} />
            {agent.name}
            {agent.isSample ? <span className="pill sample">Sample</span> : null}
          </p>
          <p className="muted">{agent.publishedPolicyVersionId ? "Ready to put on your site." : "Not on your site yet."}</p>
        </div>
        <div className="row">
          <Link className="text-link" to={`/app/agents/${agent.id}/conversations`}>
            Chats
          </Link>
          <button className="text-link" type="button" disabled={pending} onClick={() => void remove()}>
            Delete
          </button>
        </div>
      </header>
      {progressError ? <p className="banner bad">{progressError}</p> : null}
      <Steps agentId={agent.id} progress={progress} current={current} />
      <Outlet context={{ agent, reload: load, progress, mark, liveTheme, setLiveTheme, note: note || previewNote(agent, false) } satisfies AgentOutlet} />
      {onTest ? null : (
        <ChatBubble
          key={agent.id}
          agentId={agent.id}
          name={agent.name}
          theme={liveTheme}
          note={note || previewNote(agent, false)}
          placement="fixed"
          onAnswered={() => mark("test")}
        />
      )}
    </div>
  );
}

export function AgentIndex() {
  const { agent, progress } = useOutletContext<AgentOutlet>();
  return <Navigate to={stepPath(agent.id, nextStep(progress))} replace />;
}

function currentStep(pathname: string): StepId | undefined {
  if (pathname.endsWith("/knowledge")) return "knowledge";
  if (pathname.endsWith("/answers") || pathname.endsWith("/policy")) return "answers";
  if (pathname.endsWith("/test")) return "test";
  if (pathname.endsWith("/look")) return "look";
  if (pathname.endsWith("/install")) return "install";
  return undefined;
}
